import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Analytics } from '../lib/analytics';

export default function SsoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();
  const [error, setError] = useState<string | null>(null);
  const attemptedTicketRef = useRef<string | null>(null);

  const ticket = searchParams.get('__clerk_ticket');

  useEffect(() => {
    if (!isLoaded) return;

    // Already signed in — skip exchange
    if (isSignedIn) {
      navigate('/', { replace: true });
      return;
    }

    if (!ticket) {
      navigate('/', { replace: true });
      return;
    }

    // One-shot guard — prevents StrictMode double-invocation from consuming the ticket twice
    if (attemptedTicketRef.current === ticket) return;
    attemptedTicketRef.current = ticket;

    let cancelled = false;

    const exchange = async () => {
      try {
        const result = await signIn.create({ strategy: 'ticket', ticket });
        if (cancelled) return;
        await setActive({ session: result.createdSessionId });
        if (cancelled) return;
        navigate('/', { replace: true });
      } catch (err: unknown) {
        if (!cancelled) {
          setError('expired');
          Analytics.trackError({
            error_type: 'sso_ticket_exchange_failed',
            error_message: err instanceof Error ? err.message : 'Unknown error',
            component: 'SsoPage',
          });
        }
      }
    };

    exchange();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, navigate, signIn, setActive, ticket]);

  if (!isLoaded || (!error && !isSignedIn)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="mb-4" />
          <p className="text-muted-foreground">Signing you in…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">Link Expired</h2>
          <p className="text-muted-foreground mb-6">
            This link has expired. Return to DMO and click Launch Game Hub again.
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
}
